import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Printer, Download, Loader2, AlertCircle, FileText } from 'lucide-react';
import comprobanteISLRTemplateHtml from './ComprobanteISLRTemplate';

const MAX_RETRIES = 50;

const AGENTE_GAMAN = {
  nombre: 'ELECTROSOLUCIONES GAMAN C.A',
  rif: 'J-411293229',
  direccion: 'CALLE ROSCIO EDIF RESIDENCIAS MARIANA PISO PB LOCAL 3 SECTOR CENTRO SAN JUAN DE LOS MORROS GUARICO ZONA POSTAL 2301',
  telefono: '',
  ciudad: 'SAN JUAN DE LOS MORROS',
  estado: 'GUÁRICO'
};

const toISODate = (f) => (f ? new Date(f).toISOString().split('T')[0] : '');

// Retencion ISLR real (direccion='emitida') que Gaman emite a un proveedor:
// Gaman es el agente de retencion, el proveedor es el sujeto retenido. El
// monto retenido viene de fn_calcular_islr (ya neto de sustraendo si aplica),
// no se recalcula en el navegador.
//
// El comprobante es un documento fiscal venezolano: siempre en bolivares,
// sin importar en que moneda se facturo la compra. Los campos de la
// plantilla son <input type="number"> (no texto formateado), asi que se
// envia el numero ya convertido, sin separador de miles ni simbolo --
// eso es una limitacion de la plantilla, no del calculo.
const transformData = (retencion) => {
  if (!retencion || !retencion.comprobante) return null;
  if (!retencion.tasa) return { error: 'sin_tasa' };

  const tasa = parseFloat(retencion.tasa);
  const aBs = (n) => ((parseFloat(n) || 0) * tasa).toFixed(2);

  return {
    numero: retencion.comprobante,
    fechaEmision: toISODate(retencion.fecha),
    agente: AGENTE_GAMAN,
    sujeto: {
      nombre: retencion.proveedor_nombre || '',
      rif: retencion.proveedor_rif || '',
      telefono: retencion.proveedor_telefono || '',
      ciudad: '',
      estado: ''
    },
    factura: {
      fechaEmision: toISODate(retencion.fc_fecha_emision),
      numero: retencion.fc_numero_proveedor || '',
      numeroControl: retencion.fc_numero_control_proveedor || '',
      baseImponible: aBs(retencion.fc_base_imponible),
      iva: aBs(retencion.fc_impuesto)
    },
    islr: {
      codigo: retencion.islr_concepto_codigo || '',
      concepto: retencion.islr_concepto_nombre || '',
      montoObjeto: aBs(retencion.base),
      porcentaje: (parseFloat(retencion.porcentaje_aplicado || 0) * 100).toFixed(2),
      retenido: aBs(retencion.monto)
    }
  };
};

const RetencionEmitidaISLRPrintModal = ({ onClose, retencion }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);
  const datosInyectados = useRef(false);
  const retryCount = useRef(0);

  const datosCrudos = retencion ? transformData(retencion) : null;
  const datosComprobante = datosCrudos && !datosCrudos.error ? datosCrudos : null;

  const inyectarDatos = useCallback(() => {
    if (!iframeRef.current || datosInyectados.current || !datosComprobante) return;
    try {
      const win = iframeRef.current.contentWindow;
      if (win && typeof win.renderComprobanteISLR === 'function') {
        win.renderComprobanteISLR(datosComprobante);
        datosInyectados.current = true;
        setLoading(false);
      } else if (retryCount.current < MAX_RETRIES) {
        retryCount.current += 1;
        setTimeout(inyectarDatos, 100);
      } else {
        setError('No se pudo cargar el comprobante (tiempo de espera agotado)');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error al inyectar datos:', err);
      setError('Error al renderizar el comprobante');
      setLoading(false);
    }
  }, [datosComprobante]);

  useEffect(() => {
    if (!datosComprobante) {
      setError(
        !retencion
          ? 'No hay retención para generar el comprobante'
          : datosCrudos?.error === 'sin_tasa'
          ? 'Esta factura de compra no tiene tasa de cambio registrada. Sin ella no se puede convertir el comprobante a bolívares.'
          : 'Esta retención todavía no fue emitida (sin número de comprobante). Emítala antes de generar el PDF.'
      );
      setLoading(false);
    }
  }, [datosComprobante, datosCrudos, retencion]);

  const handleIframeLoad = useCallback(() => {
    inyectarDatos();
  }, [inyectarDatos]);

  const handlePrint = () => {
    if (iframeRef.current) iframeRef.current.contentWindow.print();
  };

  const handleDownload = () => {
    if (iframeRef.current) {
      if (typeof iframeRef.current.contentWindow.descargarPDF === 'function') {
        iframeRef.current.contentWindow.descargarPDF();
      } else {
        iframeRef.current.contentWindow.print();
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden">
        <div className="bg-gray-100 border-b p-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <FileText className="text-gray-600" />
              Comprobante Retención ISLR (proveedor)
            </h2>
            <p className="text-sm text-gray-500">Vista previa del comprobante</p>
          </div>
          <div className="flex gap-2">
            {!loading && !error && (
              <>
                <button onClick={handleDownload} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center gap-2">
                  <Download size={18} /> Descargar PDF
                </button>
                <button onClick={handlePrint} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2">
                  <Printer size={18} /> Imprimir
                </button>
              </>
            )}
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 font-medium flex items-center gap-2">
              <X size={18} /> Cerrar
            </button>
          </div>
        </div>

        <div className="flex-1 bg-gray-200 relative overflow-hidden flex justify-center items-center">
          {loading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
              <Loader2 size={48} className="animate-spin text-blue-600 mb-4" />
              <p className="text-lg font-medium text-gray-700">Generando documento...</p>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white">
              <AlertCircle size={64} className="text-red-500 mb-4" />
              <p className="text-xl font-bold text-gray-800 mb-2">No se puede mostrar el comprobante</p>
              <p className="text-gray-600 max-w-md text-center">{error}</p>
            </div>
          )}

          <iframe
            ref={iframeRef}
            srcDoc={comprobanteISLRTemplateHtml}
            onLoad={handleIframeLoad}
            className={`w-full h-full bg-white transition-opacity duration-300 ${loading || error ? 'opacity-0' : 'opacity-100'}`}
            title="Comprobante Retención ISLR (proveedor)"
          />
        </div>
      </div>
    </div>
  );
};

export default RetencionEmitidaISLRPrintModal;
